import axios from 'axios'
import md5 from 'md5'

const ACTION_LABELS = {
    CREATED:        'hat ein Produkt angelegt',
    UPDATED:        'hat ein Produkt bearbeitet',
    STATUS_CHANGED: 'hat den Status geändert',
    CANCELED:       'hat storniert',
    ARCHIVED:       'hat archiviert',
}

const STATUS_LABELS = {
    OPEN:               'Neu',
    IN_PROCESS:         'In Bearbeitung',
    OFFERS_AVAILABLE:   'Angebote vorhanden',
    OFFER_CONFIRMED:    'Angebot bestätigt',
    IN_PRINT:           'Im Druck',
    PRODUCTION_RELEASE: 'Produktionsfreigabe',
    IN_PRODUCTION:      'In Produktion',
    DELIVERED:          'Ausgeliefert',
    QA:                 'QA',
    COMPLETED:          'Abgeschlossen',
    CANCELED:           'Storniert',
    ARCHIVE:            'Archiviert',
}

const fmt = ts => new Date(ts).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
})

function gravatarUrl(email) {
    if (!email) return null
    return `https://www.gravatar.com/avatar/${md5(email.toLowerCase().trim())}?s=40&d=blank`
}

function personInitials(person) {
    if (!person) return '?'
    const first = person.firstName ? person.firstName.charAt(0).toUpperCase() : ''
    const last  = person.lastName  ? person.lastName.charAt(0).toUpperCase()  : ''
    return (first + last) || '?'
}

// Preferred avatar: MOCO image (avatar_url) if present, otherwise Gravatar fallback.
function avatarUrl(person) {
    if (!person) return null
    if (person.avatar_url) return person.avatar_url
    return gravatarUrl(person.email)
}

function renderAvatar(person) {
    const initial = personInitials(person)
    const url     = avatarUrl(person)
    return `<div class="ph-avatar-wrapper">
                <span class="ph-avatar-initial">${initial}</span>
                ${url ? `<img class="ph-avatar-img" src="${url}" />` : ''}
            </div>`
}

function fullName(person) {
    if (!person) return null
    return [person.firstName, person.lastName].filter(Boolean).join(' ') || null
}

async function loadPersonMap(userIds) {
    const unique = [...new Set(userIds.filter(Boolean))]
    const results = await Promise.all(
        unique.map(userId =>
            axios.get(`/api/v1/users/${userId}/person`)
                .then(({ data }) => [userId, data])
                .catch(() => [userId, null])
        )
    )
    return Object.fromEntries(results)
}

function renderFeed(data, personMap) {
    if (data.length === 0) {
        return `<div class="ui placeholder segment">
                    <div class="ui icon header">
                        <i class="history icon"></i>
                        Noch keine Einträge vorhanden
                    </div>
                </div>`
    }
    const events = data.map(entry => {
        const person = personMap[entry.userId] || null
        const name   = fullName(person) || 'Unbekannt'
        const verb   = ACTION_LABELS[entry.action] || entry.action
        const label  = `${name} ${verb}`
        const change = (entry.oldStatus && entry.newStatus)
            ? `<div class="extra text">
                   <span class="ui tiny label">${STATUS_LABELS[entry.oldStatus] || entry.oldStatus}</span>
                   <i class="arrow right icon"></i>
                   <span class="ui tiny label">${STATUS_LABELS[entry.newStatus] || entry.newStatus}</span>
               </div>`
            : ''
        return `<div class="event">
                    <div class="label">${renderAvatar(person)}</div>
                    <div class="content">
                        <div class="date">${fmt(entry.timestamp)}</div>
                        <div class="summary">${label}</div>
                        ${change}
                    </div>
                </div>`
    }).join('')
    return `<div class="ui feed">${events}</div>`
}

const AVATAR_STYLES = `
    .ui.feed .event .label .ph-avatar-wrapper {
        position: relative;
        width: 2.5em;
        height: 2.5em;
        margin-top: 4px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
    }
    .ui.feed .event .label .ph-avatar-initial {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #4b5563;
        color: #ffffff;
        font-size: 0.85rem;
        font-weight: 600;
        user-select: none;
    }
    .ui.feed .event .label .ph-avatar-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

const FLYOUT_DEFAULTS = {
    dimPage:   false,
    closable:  true,
    closeIcon: true,
}

function injectStyles() {
    if (document.getElementById('ph-avatar-styles')) return
    const style = document.createElement('style')
    style.id = 'ph-avatar-styles'
    style.textContent = AVATAR_STYLES
    document.head.appendChild(style)
}

function createFlyout(flyoutOptions) {
    const flyoutEl  = document.createElement('div')
    flyoutEl.className = 'ui right flyout'

    const closeEl   = document.createElement('i')
    closeEl.className = 'close icon'

    const headerEl  = document.createElement('div')
    headerEl.className = 'ui small header'

    const contentEl = document.createElement('div')
    contentEl.className = 'scrolling content'

    flyoutEl.appendChild(closeEl)

    flyoutEl.appendChild(headerEl)
    flyoutEl.appendChild(contentEl)
    document.body.prepend(flyoutEl)

    const $flyout = $(flyoutEl)
    $flyout.flyout({ ...FLYOUT_DEFAULTS, ...flyoutOptions })

    return { $flyout, headerEl, contentEl }
}

export default {
    install(Vue, flyoutOptions = {}) {
        injectStyles()

        const { $flyout, headerEl, contentEl } = createFlyout(flyoutOptions)

        Vue.prototype.$productHistory = {
            show(productId, productName) {
                headerEl.innerHTML = `<i class="history icon"></i> Verlauf: ${productName}`
                contentEl.innerHTML = `<div class="ui segment" style="min-height:6rem">
                    <div class="ui active inverted dimmer">
                        <div class="ui loader"></div>
                    </div>
                </div>`

                $flyout.flyout('show')

                axios.get(`/api/v1/products/${productId}/history`)
                    .then(async ({ data }) => {
                        const personMap = await loadPersonMap(data.map(e => e.userId))
                        contentEl.innerHTML = renderFeed(data, personMap)
                    })
                    .catch(() => { contentEl.innerHTML = '<div class="ui error message">Verlauf konnte nicht geladen werden.</div>' })
            },
        }
    },
}
